<template>
  <div class="agency">
    <main-section :meta-info="metaInfo" section-id="" title="" :sections="sections" image-url=""></main-section>

    <header class="agency__hero">
      <div class="agency-title">
        <h1 class="agency-city" ref="agencyCityHeading">{{ getCompanyName }}</h1>
      </div>
      <div class="office-actions">
        <form-button text="Tilaa arviokäynti" theme="primary" @click.native="openAgencyForm" tabindex="0"></form-button>
      </div>
    </header>

    <sticky-navigation :anchor-links="anchorLinks">
      <a class="nav-link" @click.prevent="openAgencyForm">
        <i class="anchor-link__icon icon-speech-bubble-circled"></i>
        <span class="long">Viesti</span>
      </a>
    </sticky-navigation>

    <div id="agency-news">
      <template v-if="news?.length > 0">
        <div v-for="newsItem in news" :key="newsItem.id" class="agency-news-container">
          <p class="title">{{ newsItem.title }}</p>
          <p class="content">{{ newsItem.body }}</p>
          <div class="action-button-container">
            <template v-if="newsItem.links.length > 0">
              <a
                v-for="link in newsItem.links"
                :key="link.url"
                :href="link.url"
                class="button"
                :target="link.url.startsWith('#') || link.url.startsWith('https://op-koti.fi') ? '_self' : '_blank'"
              >
                {{ link.title }}
              </a>
            </template>
          </div>
        </div>
      </template>
    </div>

    <div :id="agencyIntroId">
      <statistics-info-container :content="getStatisticsInfoContainer" :fourItemsInRow="true" :has-margin-top="true" />
      <form-button
        class="evaluation-button"
        text="Tilaa arviokäynti"
        theme="primary"
        @click.native="openAgencyForm"
      ></form-button>

      <div id="agency-description" v-if="getDescriptionParagraphs && getDescriptionParagraphs.length > 0">
        <div class="agency-description-container">
          <h1 class="title">{{ getCompanyName }}</h1>
          <template v-for="(paragraph, idx) in getDescriptionParagraphs" :key="`paragraph-${idx}`">
            <p>{{ paragraph }}</p>
          </template>
          <div class="agency-description-contact">
            <form-button text="Tilaa arviokäynti" theme="primary" @click.native="openAgencyForm"></form-button>
          </div>
        </div>
      </div>
    </div>

    <CompanyContact v-if="!!company && company?.vatCode" :company="company"></CompanyContact>

    <offices
      :scrollToId="officesId"
      :agencyId="companyData?.id"
      :offices="getCompanyOffices"
      title="Toimipisteemme:"
    ></offices>

    <div class="agency__pricing" v-if="Object.keys(getCompanyPricing).length !== 0">
      <div class="pricing-wrapper">
        <h2 :id="pricingId">Palveluhinnasto</h2>
        <a class="price-list-link" :href="getCompanyPricing">
          <i class="icon-instructions-circled"></i>
          <span>
            Lataa palveluhinnasto
            <br />
            (PDF)
          </span>
        </a>
      </div>
    </div>
    <div class="line-divider" v-else></div>

    <section class="agency__listings" v-if="listings?.length">
      <div class="agency__listings__wrapper">
        <suggested-listings :title="`Uusimmat myytävät asunnot - ${getCompanyName}`" :suggestions="listings">
          <template v-slot:footnote>
            <div>
              <action-link :to="{ path: apartments, query: { companyId: getCompanyId } }">
                Näytä kaikki välittämämme asunnot
              </action-link>
            </div>
          </template>
        </suggested-listings>
      </div>
    </section>

    <call-to-action :actions="callToActionContent"></call-to-action>
  </div>
  <dialogue identifier="company-agency-dialogue" v-if="companyData?.id">
    <template #dialogueContent>
      <agency-contact :agencyId="companyData?.id" :office="office" :person="person"></agency-contact>
    </template>
  </dialogue>
</template>

<script lang="ts">
import * as actions from '@/app/store/action-types';
import Offices from '@/app/components/offices/offices.vue';
import CompanyContact from '@/app/components/companies/company-contact.vue';
import { search } from '@/app/routes';
import config from '@/config';
import MainSection from '@/app/components/main-section/main-section.vue';
import ActionLink from '@/app/components/action-link/action-link.vue';
import { defineAsyncComponent, computed, defineComponent, ref } from 'vue';
import CallToAction from '@/app/components/main-section/call-to-action.vue';
import useCompanyAgencyData from '@/app/data/useCompanyAgencyData';
import useAgencyNewsData from '@/app/data/useAgencyNewsData';
import { useListingsData } from '@/app/data/useListingsData';
import isSSR from '@/utils/isSSR';
import useSafeRoute, { getRouteParamAsString } from '@/app/composables/useSafeRoute';
import logger from '@/app/logger';
import type { CtaLinkType } from '@/schemas/landingPageSchema';
import { useFocusWithWatch } from '@/app/composables/useFocusWithWatch';

export default defineComponent({
  name: 'corporate-agency',
  components: {
    StickyNavigation: defineAsyncComponent(() => import('@/app/components/sticky-navigation/sticky-navigation.vue')),
    CallToAction,
    ActionLink,
    Dialogue: defineAsyncComponent(() => import('@/app/components/dialogue/dialogue.vue')),
    AgencyContact: defineAsyncComponent(() => import('@/app/components/agency-contact/agency-contact.vue')),
    SuggestedListings: defineAsyncComponent(() => import('@/app/components/suggested-listings/suggested-listings.vue')),
    FormButton: defineAsyncComponent(() => import('@/app/components/form-button/form-button.vue')),
    CompanyContact,
    Offices,
    StatisticsInfoContainer: defineAsyncComponent(
      () => import('@/app/components/statistics-info-container/statistics-info-container.vue')
    ),
    MainSection,
  },
  setup() {
    // Get agency
    const route = useSafeRoute();
    const id = getRouteParamAsString(route.value.params.id) || '';
    const { data: companyData } = useCompanyAgencyData(id);
    const companyId = computed(() => companyData?.value?.id);

    // Get agency news
    const enabled = computed(() => !isSSR && companyId.value != null && companyId.value != '');
    const { data: newsData } = useAgencyNewsData(companyId, enabled);

    // Get agency listings
    const filters = computed(() => ({
      companyId: companyId,
      featureGroup: 'apartment',
      limit: 4,
    }));
    const { data: listingsData } = useListingsData(filters, enabled);
    const listings = computed(() => listingsData?.value?.listings);

    const agencyCityHeading = ref<HTMLElement | null>(null);

    useFocusWithWatch(companyData, agencyCityHeading);

    return {
      companyData,
      newsData,
      listings,
      agencyCityHeading,
    };
  },
  data: () => ({
    person: null as any,
    office: null as any,
    officesId: 'company-agency-offices-header',
    agencyIntroId: 'agency-introduction',
    pricingId: 'agency-pricing',
    callToActionContent: [
      {
        type: 'link',
        name: 'Myytävät asunnot',
        route: '/myytavat/asunnot',
        iconClass: 'icon-search-circled',
      },
      {
        type: 'contact-company-agency',
        name: 'Ota yhteyttä',
        iconClass: 'icon-speech-bubble-circled',
      },
    ] as CtaLinkType[],
    formAnalytics: {
      eVar21: 'kiinteistovalitys',
      eVar22: 'viesti-valittajalle',
    },
  }),
  methods: {
    formCompanyDescription() {
      return `Luotettavaa ja asiantuntevaa kiinteistönvälitystä ${this.getCompanyName}. Ota yhteyttä – tehdään arvio sekä myyntisuunnitelma asunnostasi.`;
    },
    openAgencyForm() {
      this.person = null;
      this.$bus.$emit('openDialogue', 'company-agency-dialogue');
    },
    sendPageAnalytics() {
      void this.$store.dispatch(actions.analytics.set, {
        pageName: `kiinteistonvalitys:yhtio:${this.$route.params.id}`,
        agencyName: this.companyData?.name,
        eVar17: this.metaInfo.title,
      });

      void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.pageView);
    },
    openContactFormIfNecessary() {
      const contact = this.$route.query.contact || '';
      if (contact.toLowerCase() === 'true') {
        this.openAgencyForm();
      }
    },
  },
  watch: {
    companyData: {
      handler(newVal) {
        if (!isSSR && newVal?.name) {
          logger.info('Sending analytics for pageName:' + newVal.name);
          this.sendPageAnalytics();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.$bus.$on(
      'openPersonContactDialogue',
      (event) => {
        this.person = event.person;
        this.office = event.office;
        this.$bus.$emit('openDialogue', 'company-agency-dialogue');
      },
      this
    );

    this.$bus.$on(
      'dialogueClosed',
      () => {
        this.person = null;
        this.office = null;
      },
      this
    );

    this.openContactFormIfNecessary();
  },
  computed: {
    news() {
      return this.newsData;
    },
    company() {
      return this.companyData;
    },
    selectedCompany() {
      return this.companyData;
    },
    metaInfo() {
      return {
        title: `Kiinteistönvälitys ${this.getCompanyName} - OP Koti` || 'Toimipistettä ei löytynyt - OP Koti',
        meta: [{ vmid: 'description', name: 'description', content: this.formCompanyDescription() }],
      };
    },
    anchorLinks() {
      const links = [
        {
          scrollToId: this.agencyIntroId,
          iconClass: 'icon-info-circled',
          label: 'Tietoa meistä',
        },
        {
          scrollToId: this.officesId,
          iconClass: 'icon-bankoffice-circled',
          label: 'Toimipisteet',
        },
      ];

      if (this.selectedCompany && this.selectedCompany.pricing) {
        links.push({
          scrollToId: this.pricingId,
          iconClass: 'icon-instructions-circled',
          label: 'Palveluhinnasto',
        });
      }

      return links;
    },
    getCompanyPricing() {
      return this.company && this.company?.pricing ? this.company?.pricing : {};
    },
    getDescriptionParagraphs() {
      if (this.company && this.company?.description && this.company?.description.length > 0) {
        return this.company.description.filter((paragraph: any) => paragraph.length > 0);
      }
    },
    getCompanyOffices() {
      return this.company && this.company?.offices ? this.company?.offices : [];
    },
    getCompanyId() {
      return this.selectedCompany && this.selectedCompany.id ? this.selectedCompany.id : 0;
    },
    getCompanyName() {
      return this.companyData?.name || '';
    },
    sections() {
      return [];
    },
    getStatisticsInfoContainer() {
      return {
        title:
          'OP Koti on tutkitusti ykkönen kaikilla tärkeimmillä tekijöillä, joilla valitaan kiinteistönvälittäjä asunnon myyntiin:',
        sections: [
          { image: 'https://op-koti.fi/assets/icons/icon-002210.png ', description: 'Luotetuin' },
          {
            image: 'https://op-koti.fi/assets/icons/icon-000310.png ',
            description: 'Asiantuntevin palvelu',
          },
          {
            image: 'https://op-koti.fi/assets/icons/icon-000920.png ',
            description: 'Paras paikallistuntemus',
          },
          {
            image: 'https://op-koti.fi/assets/icons/icon-000340.png ',
            description: 'Suosituin valinta',
          },
        ],
      };
    },
    apartments() {
      return search.sale.routes.find((route) => route.id === config.testing.NAVIGATION.BUYING.APARTMENTS)?.path || '';
    },
  },
});
</script>
